<template>
  <modal
    width="560px"
    bodyOverflow="auto"
    :title="
      form.id ? t('Edit Custom Conversion Event') : t('Add Custom Conversion Event')
    "
    @close="checkClose"
    read-article-url="https://help.bemob.com/docs/custom-conversions"
  >
    <div>
      <div>
        {{
          t(
            "Specify a name for the custom conversion event as well as corresponding value(s) of the cet postback parameter. The custom conversion event name will be displayed in reports. Each parameter's value should be added in a separate line.",
          )
        }}
      </div>
      <ui-info
        class="mt-8 mb-8"
        :html="
          t(
            '<b>Example.</b> For the <b>Sign up</b> custom conversion event with the parameter’s values such as <b>signup</b> and <b>registration</b>, every postback fired with &cet=<b>signup</b> or &cet=<b>registration</b> will be counted as a new conversion. Those conversions will be categorized as the <b>Sign up</b> event.',
          )
        "
      />
      <ui-input
        v-model="form.name"
        :label="t('Custom Conversion Event Name') + '*'"
        :label-help="
          t('Specify the name of custom conversion which will be displayed in the stats.')
        "
        :placeholder="t('Event Name')"
        :error-messages="fieldErrors.name"
        @change="clearError('name')"
      />

      <ui-select
        v-model="form.values"
        v-model:search-input="search"
        :items="form.values"
        :placeholder="t('Separated by new line')"
        :label="t('Values of The cet Parameter') + '*'"
        :label-help="
          t(
            'Introduce the value of custom conversion which will be passed via et parameter to BeMob.',
          )
        "
        multiple
        :error-messages="fieldErrors.values"
        chips
        combobox
        multi-line
        :hideNoData="false"
      >
        <template #no-data>
          <div
            class="ui-select-no-data-kbd"
            v-html="t('Type the value and press <kbd>enter</kbd>')"
          />
        </template>
      </ui-select>

      <ui-checkbox
        :help-tooltip="
          t(
            'If this option is selected the custom conversion will be included in the conversions amount.',
          )
        "
        v-model="form.includeInConversions"
        :label="t('Include in <b>Conversions</b> Column')"
      />

      <ui-checkbox
        v-model="form.includeInRevenue"
        :label="t('Include in <b>Revenue</b> Column')"
        :help-tooltip="
          t(
            'If this option is selected the revenue on custom conversion will be included in the revenue value.',
          )
        "
      />

      <ui-checkbox
        :help-tooltip="
          t(
            'If this option is selected it will be possible to forward the value of custom conversion to the side of your traffic source via postback.',
          )
        "
        @change="sendPostbacksChanged"
        v-model="form.sendPostback"
        :label="t('Send Postbacks to Traffic Source')"
      />

      <ui-checkbox
        class="pl-16"
        @change="sendPostbacksChanged"
        v-model="form.includeInCost"
        :disabled="!form.sendPostback"
        :label="t('Include in <b>Cost</b> column')"
        :help-tooltip="
          t(
            'If this option is selected the cost of custom conversion will be counted in the Cost value.',
          )
        "
      />

      <ui-checkbox
        v-model="form.includeInDailyCap"
        :help-tooltip="
          t(
            'If this option is selected, this custom conversion will be counted in the daily cap value configured on the level of offer settings.',
          )
        "
        :label="t('Include in conversions <b>daily cap</b> value')"
      />
    </div>

    <template #footer>
      <section class="justify-space-between align-center xs12 error-wrapper d-flex">
        <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        <div class="d-flex justify-end align-center">
          <ui-update-info v-if="form.id" :data="form" />
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="loading"
            @click="checkClose"
          />
          <ui-button
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            @click="save"
            >{{ t('Save') }}</ui-button
          >
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import _ from 'lodash';
import md5 from 'md5';
import { useApiFieldErrors, useI18n, useMixpanel } from '@/composables';
import { useStore } from '@/store';
import $emitter from '@/services/emitter';
import { getSupportedMetricsApiRequest } from '@/services/api/report-api';
import {
  getCustomConversionsApiRequest,
  postCustomConversionApiRequest,
  putCustomConversionApiRequest,
} from '@/services/api/custom-conversions-api';

type CustomConversionForm = {
  name: string;
  values: string[];
  id: string;
  includeInConversions: boolean;
  includeInCost: boolean;
  sendPostback: boolean;
  includeInRevenue: boolean;
  includeInDailyCap: boolean;
  updated?: string;
  created?: string;
  updatedBy?: string;
};

const props = defineProps<{
  data?: Partial<CustomConversionForm>;
}>();

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const { trackMixpanel } = useMixpanel();
const store = useStore();
const loading = ref(false);
const search = ref('');
const oldHash = ref('');

const { fieldErrors, errors, errorsControlActive, clearErrors, applyApiErrors } =
  useApiFieldErrors();

const getDefaultForm = (): CustomConversionForm => ({
  name: '',
  values: [],
  id: '',
  includeInConversions: false,
  includeInCost: false,
  sendPostback: false,
  includeInRevenue: false,
  includeInDailyCap: false,
});

const form = reactive<CustomConversionForm>(getDefaultForm());

onMounted(() => {
  const nextForm = _.isEmpty(props.data)
    ? getDefaultForm()
    : { ...getDefaultForm(), ...props.data };
  Object.assign(form, nextForm);
  oldHash.value = getCurrentHash();
});

const sendPostbacksChanged = () => {
  if (!form.sendPostback) {
    form.includeInCost = false;
  }
};

const getCurrentHash = () => md5(JSON.stringify(form));

const clearError = (field: keyof CustomConversionForm) => {
  const newValue = { ...fieldErrors.value };
  delete newValue[field];
  fieldErrors.value = newValue;
};

const save = async () => {
  clearErrors();
  loading.value = true;
  store.setLoading(true);
  try {
    if (form.id) {
      await putCustomConversionApiRequest(form);
    } else {
      await postCustomConversionApiRequest(form);
    }
    const metrics = await getSupportedMetricsApiRequest();
    store.setMetrics(metrics);
    const customEvents = await getCustomConversionsApiRequest();
    store.setCustomEvents(customEvents);
    trackMixpanel(form.id ? 'custom_event_edit' : 'custom_event_create', {
      name: form.name,
      values: form.values,
    });
    close();
  } catch (error: unknown) {
    applyApiErrors(error, { nested: true });
    return Promise.reject(error);
  } finally {
    loading.value = false;
    store.setLoading(false);
  }
};

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: { onConfirm: () => close() },
    });
    return;
  }
  close();
};

const close = () => {
  store.setLoading(false);
  emit('close');
};
</script>

<style lang="scss" scoped>
.error-wrapper {
  width: 100%;
}

.create-custom-dialog {
  height: 100%;
  max-height: 600px !important;

  :deep(.v-card) {
    height: 100%;
    max-height: 600px;
  }
}

.create-custom-dialog__details {
  background: var(--color-gray5);
}

.ui-select-no-data-kbd {
  box-sizing: border-box;
  min-height: 32px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 24px;
  color: var(--color-graphite);

  :deep(kbd) {
    display: inline-block;
    border-radius: 3px;
    white-space: pre-wrap;
    font-family: monospace, monospace;
    font-size: 85%;
    font-weight: 900;
    line-height: 24px;
    background: #616161;
    color: #fff;

    &::before,
    &::after {
      content: '\00a0';
      letter-spacing: -1px;
    }
  }
}
</style>
